"use client"
import { Modal, Button } from "react-bootstrap"
import { Download, X, FileText, ImageIcon, File } from "lucide-react"
import { useTranslation } from "../hooks/use-translation"

interface AttachmentPreviewModalProps {
  show: boolean
  onHide: () => void
  attachmentUrl: string
  attachmentName: string
  attachmentType: string
}

export default function AttachmentPreviewModal({
  show,
  onHide,
  attachmentUrl,
  attachmentName,
  attachmentType,
}: AttachmentPreviewModalProps) {
  const { t } = useTranslation()

  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith("image/")) {
      return <ImageIcon className="size-6" />
    } else if (fileType.includes("pdf")) {
      return <FileText className="size-6" />
    } else {
      return <File className="size-6" />
    }
  }

  const isImage = attachmentType.startsWith("image/")
  const isPdf = attachmentType.includes("pdf")

  const handleDownload = () => {
    const link = document.createElement("a")
    link.href = attachmentUrl
    link.download = attachmentName
    link.target = "_blank"
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header className="d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center gap-2">
          {getFileIcon(attachmentType)}
          <Modal.Title className="fs-5 mb-0 text-truncate">{attachmentName}</Modal.Title>
        </div>
        <Button variant="outline-secondary" onClick={onHide} size="sm">
          <X className="size-4" />
        </Button>
      </Modal.Header>

      <Modal.Body className="p-0">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
          {isImage ? (
            <img
              src={attachmentUrl || "/placeholder.svg"}
              alt={attachmentName}
              className="img-fluid"
              style={{ maxHeight: "70vh", maxWidth: "100%" }}
            />
          ) : isPdf ? (
            <div className="w-100 h-100" style={{ minHeight: "500px" }}>
              <iframe
                src={attachmentUrl}
                width="100%"
                height="500px"
                style={{ border: "none" }}
                title={attachmentName}
              />
            </div>
          ) : (
            <div className="text-center p-5">
              <div className="mb-3">{getFileIcon(attachmentType)}</div>
              <h5 className="mb-2">{attachmentName}</h5>
              <p className="text-muted mb-3">{t("previewNotAvailable")}</p>
              <Button variant="primary" onClick={handleDownload}>
                <Download className="size-4 me-2" />
                {t("downloadToView")}
              </Button>
            </div>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer className="d-flex justify-content-between">
        <small className="text-muted">
          {t("fileType")}: {attachmentType}
        </small>
        <Button variant="primary" onClick={handleDownload}>
          <Download className="size-4 me-1" />
          {t("download")}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
